/**
 * Phone-number verification by SMS code, via Google Identity Platform.
 *
 * Identity Platform is used only to prove the user owns a phone number — Keycloak stays
 * the identity provider. The browser never talks to Identity Platform itself: it solves
 * an invisible reCAPTCHA, and our backend sends and confirms the code with the API key
 * server-side, so the key never appears in the browser or its network traffic. Confirming
 * returns an opaque proof id (Google's token stays on the backend), which goes back with
 * the phone number for the backend to check (see backend/app/auth/phone_verification.py).
 *
 * The whole feature is switched on/off by the backend (GET /config/phone-verification),
 * which also hands over the reCAPTCHA site key — public by design. The reCAPTCHA script
 * is loaded lazily, so nothing is downloaded while the feature is off.
 */
import { getBackendUrl } from '../utils/utils';
import { createLogger } from '../utils/logger';

const logger = createLogger('phoneVerification');

export interface PhoneVerificationConfig {
  enabled: boolean;
  recaptcha_site_key?: string;
}

/** A code sent to a number; pass it back with the code the user typed. */
export interface PhoneCodeSession {
  sessionInfo: string;
}

let configPromise: Promise<PhoneVerificationConfig> | null = null;

/**
 * Cached for the page's lifetime once it loads. A failed fetch rejects and is *not*
 * cached: guessing "off" would hide the verification controls while the backend still
 * demands a code, leaving the user stuck — so callers treat a failure as "unknown" and
 * try again.
 */
export function getPhoneVerificationConfig(): Promise<PhoneVerificationConfig> {
  if (!configPromise) {
    configPromise = fetch(`${getBackendUrl()}/config/phone-verification`)
      .then((r) => {
        if (!r.ok) throw new Error(`Phone verification config request failed (${r.status})`);
        return r.json() as Promise<PhoneVerificationConfig>;
      })
      .catch((err) => {
        logger.warn('Could not load phone verification config', err);
        configPromise = null;
        throw err;
      });
  }
  return configPromise;
}

/** Forget the cached config so the next call asks the backend again. */
export function invalidatePhoneVerificationConfig(): void {
  configPromise = null;
}

// ── reCAPTCHA v2 (invisible) ──────────────────────────────────────────────────

interface Grecaptcha {
  render(container: HTMLElement, params: Record<string, unknown>): number;
  execute(widgetId: number): void;
  reset(widgetId: number): void;
}

declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
    __phoneVerificationRecaptchaLoaded?: () => void;
  }
}

const RECAPTCHA_TIMEOUT_MS = 2 * 60 * 1000;
let recaptchaPromise: Promise<Grecaptcha> | null = null;

function loadRecaptcha(): Promise<Grecaptcha> {
  if (window.grecaptcha?.render) return Promise.resolve(window.grecaptcha);
  if (!recaptchaPromise) {
    recaptchaPromise = new Promise<Grecaptcha>((resolve, reject) => {
      window.__phoneVerificationRecaptchaLoaded = () => resolve(window.grecaptcha as Grecaptcha);
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js?onload=__phoneVerificationRecaptchaLoaded&render=explicit';
      script.async = true;
      script.onerror = () => {
        recaptchaPromise = null;
        script.remove();
        reject(new Error('The security check could not load. Please check your connection and try again.'));
      };
      document.head.appendChild(script);
    });
  }
  return recaptchaPromise;
}

interface PendingCheck {
  resolve: (token: string) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

/** One widget per container, rendered on first use and reset for each later check. */
const widgets = new WeakMap<HTMLElement, { id: number; pending: PendingCheck | null }>();

/**
 * Run the invisible bot check in ``container`` and return its token. The widget is
 * rendered once per container and reset for each later check (a token is single-use),
 * so resends don't pile up widgets. Usually resolves without any UI; Google may show
 * an image challenge first.
 */
async function getRecaptchaToken(container: HTMLElement, siteKey: string): Promise<string> {
  const grecaptcha = await loadRecaptcha();
  let widget = widgets.get(container);
  if (!widget) {
    const state: { id: number; pending: PendingCheck | null } = { id: -1, pending: null };
    // Settles whichever check is in flight; the callbacks outlive any one of them.
    const settle = (fn: (p: PendingCheck) => void) => {
      const p = state.pending;
      state.pending = null;
      if (p) { clearTimeout(p.timer); fn(p); }
    };
    state.id = grecaptcha.render(container, {
      sitekey: siteKey,
      size: 'invisible',
      callback: (token: string) => settle((p) => p.resolve(token)),
      'error-callback': () => settle((p) => p.reject(new Error('The security check failed. Please refresh the page and try again.'))),
      'expired-callback': () => settle((p) => p.reject(new Error('The security check expired. Please try again.'))),
    });
    widgets.set(container, state);
    widget = state;
  } else {
    grecaptcha.reset(widget.id);
  }
  const current = widget;
  // A check still in flight (a challenge left open) is superseded by this one.
  if (current.pending) {
    clearTimeout(current.pending.timer);
    current.pending.reject(new Error('The security check was restarted.'));
  }
  return new Promise<string>((resolve, reject) => {
    // There's no callback for a challenge the user closes, so don't wait forever.
    const timer = setTimeout(() => {
      current.pending = null;
      reject(new Error('The security check timed out. Please try again.'));
    }, RECAPTCHA_TIMEOUT_MS);
    current.pending = { resolve, reject, timer };
    grecaptcha.execute(current.id);
  });
}

// ── Backend calls ─────────────────────────────────────────────────────────────

/** POST to our backend; throws the backend's ``detail`` (or ``fallback``) on failure. */
async function postJson<T>(path: string, body: unknown, fallback: string, authToken?: string | null): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${getBackendUrl()}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error('Network error. Check your connection and try again.');
  }
  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(typeof result?.detail === 'string' && result.detail ? result.detail : fallback);
  }
  return result as T;
}

/**
 * Who is asking for a code. The backend texts only a signed-in user (``authToken``) or,
 * during signup, someone whose ``email`` already passed its OTP — and refuses a number
 * another account uses before paying for the SMS.
 */
export interface PhoneSendCredentials {
  authToken?: string | null;
  email?: string;
}

/**
 * Run the bot check and text a 6-digit code to ``phoneE164``. ``recaptchaContainer`` is an
 * element the invisible reCAPTCHA can attach to; it must stay mounted until this resolves.
 */
export async function sendPhoneCode(
  phoneE164: string,
  recaptchaContainer: HTMLElement,
  credentials: PhoneSendCredentials = {},
): Promise<PhoneCodeSession> {
  const config = await getPhoneVerificationConfig();
  if (!config.enabled || !config.recaptcha_site_key) throw new Error('Phone verification is not enabled.');
  const recaptchaToken = await getRecaptchaToken(recaptchaContainer, config.recaptcha_site_key);
  const { session_info } = await postJson<{ session_info: string }>(
    '/auth/phone/send-code',
    {
      phone: phoneE164,
      recaptcha_token: recaptchaToken,
      ...(credentials.email ? { email: credentials.email } : {}),
    },
    'We couldn’t send the code. Please try again.',
    credentials.authToken,
  );
  return { sessionInfo: session_info };
}

/** Confirm the code; returns the opaque proof id the backend accepts for the number. */
export async function confirmPhoneCode(session: PhoneCodeSession, code: string): Promise<string> {
  const { phone_verification_token } = await postJson<{ phone_verification_token: string }>(
    '/auth/phone/verify-code',
    { session_info: session.sessionInfo, code },
    'Verification failed. Please try again.',
  );
  return phone_verification_token;
}
